// Which financial underlying, commodity or index a series tracks, read from its ticker. Apart from the chart
// code that renders them (components/Graph) because market tiles and entity transforms classify series too.

import { toVariableLengthFloat } from 'utils/caseing';
import { FINANCIAL_NAME_TO_COIN } from 'utils/cryptoChartData';

export type CommodityFinancialName =
    | 'US2Y'
    | 'US5Y'
    | 'US10Y'
    | 'US30Y'
    // Kalshi American Power Index - Midterms, in points from -50 (all Democratic) to +50 (all Republican).
    | 'KPOWMID'
    | 'BRENT'
    | 'COCOA'
    | 'COFFEE'
    | 'COPPER'
    | 'CORN'
    | 'DJI'
    | 'GOLD'
    | 'HOIL'
    | 'LCATTLE'
    | 'LITHIUM'
    | 'NATGAS'
    | 'NICKEL'
    | 'OIL'
    | 'PALLADIUM'
    | 'PLATINUM'
    | 'SILVER'
    | 'SOYBEANS'
    | 'SUGAR'
    | 'WHEAT'
    | 'WTI'
    // Ornn GPU compute prices, $/GPU-hour.
    | 'H100_SXM'
    | 'H200'
    | 'A100_SXM4'
    | 'B200'
    | 'RTX_5090'
    // Forex pairs — served by the commodities live-data lane (Pyth FX spot
    // feeds); keep in sync with svc-live-data/adapters/commodities/ticker.go.
    | 'EURUSD'
    | 'USDJPY'
    | 'GBPUSD'
    | 'AUDUSD'
    | 'NZDUSD'
    | 'USDCAD'
    | 'USDCHF'
    | 'USDMXN'
    | 'USDBRL'
    | 'USDINR'
    | 'USDNOK'
    | 'USDSEK';

interface EventFinancialDetectionInput {
    seriesTicker?: string;
}

type CompanyKpiDetectionInput = {
    series?: {
        productMetadata?: {
            subcategories?: Record<string, string[]>;
        };
    } | null;
} | null;

const COMMODITY_DEFINITIONS: Array<{
    aliases: string[];
    financialName: CommodityFinancialName;
    seriesPattern: RegExp;
}> = [
    { aliases: ['BRENT'], financialName: 'BRENT', seriesPattern: /^KXBRENT(?:D|W|MON|15M)?$/ },
    { aliases: ['COCOA'], financialName: 'COCOA', seriesPattern: /^KXCOCOA(?:D|W|MON)?$/ },
    { aliases: ['COFFEE'], financialName: 'COFFEE', seriesPattern: /^KXCOFFEE(?:D|W|MON)?$/ },
    { aliases: ['COPPER'], financialName: 'COPPER', seriesPattern: /^KXCOPPER(?:D|W|MON|15M)?$/ },
    { aliases: ['CORN'], financialName: 'CORN', seriesPattern: /^KXCORN(?:D|W|MON)?$/ },
    // Dow Jones hourly ladders — served by the commodities live-data lane
    // (Pyth 24/7 US30 index), keep in sync with
    // svc-live-data/adapters/commodities/ticker.go.
    // KXDJI = hourly ladders; KXDJIA = monthly/annual "how high" ranges.
    { aliases: ['DJI', 'DOWJONES'], financialName: 'DJI', seriesPattern: /^KXDJIA?$/ },
    { aliases: ['GOLD'], financialName: 'GOLD', seriesPattern: /^KXGOLD(?:D|H|W|MON|PRICE|15M)?$/ },
    { aliases: ['HOIL', 'HEATINGOIL'], financialName: 'HOIL', seriesPattern: /^KXHOIL(?:D|W|MON)?$/ },
    {
        aliases: ['LCATTLE', 'LIVECATTLE'],
        financialName: 'LCATTLE',
        seriesPattern: /^KXLCATTLE(?:D|W|MON)?$/,
    },
    { aliases: ['LITHIUM'], financialName: 'LITHIUM', seriesPattern: /^KXLITHIUM(?:W|MON)?$/ },
    {
        aliases: ['NATGAS', 'NATURALGAS'],
        financialName: 'NATGAS',
        seriesPattern: /^KXNATGAS(?:D|W|MON|15M)?$/,
    },
    { aliases: ['NICKEL'], financialName: 'NICKEL', seriesPattern: /^KXNICKEL(?:D|W|MON)?$/ },
    { aliases: ['OIL'], financialName: 'OIL', seriesPattern: /^KXOIL(?:W)?$/ },
    { aliases: ['PALLADIUM'], financialName: 'PALLADIUM', seriesPattern: /^KXPALLADIUM(?:D|H|W|MON|15M)?$/ },
    { aliases: ['PLATINUM'], financialName: 'PLATINUM', seriesPattern: /^KXPLATINUM(?:D|H|W|MON|15M)?$/ },
    { aliases: ['SILVER'], financialName: 'SILVER', seriesPattern: /^KXSILVER(?:D|H|W|MON|15M)?$/ },
    {
        aliases: ['SOYBEAN', 'SOYBEANS'],
        financialName: 'SOYBEANS',
        seriesPattern: /^KXSOYBEANS?(?:D|W|MON)?$/,
    },
    { aliases: ['SUGAR'], financialName: 'SUGAR', seriesPattern: /^KXSUGAR(?:D|W|MON)?$/ },
    { aliases: ['WHEAT'], financialName: 'WHEAT', seriesPattern: /^KXWHEAT(?:D|W|MON)?$/ },
    { aliases: ['WTI', 'CRUDEOIL'], financialName: 'WTI', seriesPattern: /^KXWTI(?:E|EU|H|W|MAXM?|MINM?|15M)?$/ },
    // Ornn GPU series — keep in sync with svc-live-data/adapters/commodities/ornn/ticker.go.
    { aliases: ['H100', 'H100SXM'], financialName: 'H100_SXM', seriesPattern: /^KXH100(?:W|MON|Q|MAX|MIN)?$/ },
    { aliases: ['H200'], financialName: 'H200', seriesPattern: /^KXH200(?:W|MON|Q|MAX|MIN)?$/ },
    { aliases: ['A100', 'A100SXM4'], financialName: 'A100_SXM4', seriesPattern: /^KXA100(?:W|MON|Q|MAX|MIN)?$/ },
    { aliases: ['B200'], financialName: 'B200', seriesPattern: /^KXB200(?:W|MON|Q|MAX|MIN)?$/ },
    { aliases: ['RTX5090', 'RTX_5090'], financialName: 'RTX_5090', seriesPattern: /^KXRTX5090(?:W|MON|Q|MAX|MIN)?$/ },

    // Treasury 15-minute yields use qualified Pyth interest-rate feeds.
    { aliases: ['US2Y'], financialName: 'US2Y', seriesPattern: /^KX2YRRATE15M$/ },
    { aliases: ['US5Y'], financialName: 'US5Y', seriesPattern: /^KX5YRRATE15M$/ },
    { aliases: ['US10Y'], financialName: 'US10Y', seriesPattern: /^KX10YRRATE15M$/ },
    { aliases: ['US30Y'], financialName: 'US30Y', seriesPattern: /^KX30YRRATE15M$/ },

    // KPOW Midterms (the "at least" ladder and the up/down series) chart Pyth's published index — keep in sync
    // with svc-live-data/adapters/commodities/ticker.go.
    { aliases: ['KPOWMID', 'KPOW'], financialName: 'KPOWMID', seriesPattern: /^KXKPOWMID(?:H|D|15M)?$/ },

    // Forex pairs, served by the commodities live-data lane (Pyth FX spot
    // feeds — keep in sync with svc-live-data/adapters/commodities/ticker.go).
    // Each pattern covers the pair's listed series families: 15-minute knockouts,
    // active *AW weeklies and daily ranges, plus dormant hourlies/up-downs, yearly
    // highs/lows, and settled one-offs, so historical pages chart too.
    // NOTE: this per-pair whitelist (and this whole per-series mapping) is
    // fine at a dozen pairs — when it becomes too unmanageable, do away with
    // it and drive detection off the events' product_metadata
    // live_data_event_type instead, which the backend can stamp at creation.
    {
        aliases: ['EURUSD'],
        financialName: 'EURUSD',
        seriesPattern: /^KXEURUSD(?:15M|AH|AW|D|E|EOY|H|HA|MAX|MIN|PARITY|Q|W)?$/,
    },
    { aliases: ['USDJPY'], financialName: 'USDJPY', seriesPattern: /^KXUSDJPY(?:15M|AH|AW|D|E|EU|H|MAX|MIN|Q|W)?$/ },
    { aliases: ['GBPUSD'], financialName: 'GBPUSD', seriesPattern: /^KXGBPUSD(?:15M|AW|D|E|H)?$/ },
    { aliases: ['AUDUSD'], financialName: 'AUDUSD', seriesPattern: /^KXAUDUSD(?:15M|AW|H|Q|W)?$/ },
    { aliases: ['NZDUSD'], financialName: 'NZDUSD', seriesPattern: /^KXNZDUSD(?:AW)?$/ },
    { aliases: ['USDCAD'], financialName: 'USDCAD', seriesPattern: /^KXUSDCAD(?:15M|AW|W)?$/ },
    { aliases: ['USDCHF'], financialName: 'USDCHF', seriesPattern: /^KXUSDCHF(?:AW)?$/ },
    { aliases: ['USDMXN'], financialName: 'USDMXN', seriesPattern: /^KXUSDMXN(?:15M|AW)?$/ },
    { aliases: ['USDBRL'], financialName: 'USDBRL', seriesPattern: /^KXUSDBRL(?:AW|MAX|MAXM)?$/ },
    { aliases: ['USDINR'], financialName: 'USDINR', seriesPattern: /^KXUSDINR(?:AW)?$/ },
    { aliases: ['USDNOK'], financialName: 'USDNOK', seriesPattern: /^KXUSDNOK(?:AW)?$/ },
    { aliases: ['USDSEK'], financialName: 'USDSEK', seriesPattern: /^KXUSDSEK(?:AW)?$/ },
];

const COMMODITY_FINANCIAL_NAMES = new Set<CommodityFinancialName>(
    COMMODITY_DEFINITIONS.map((definition) => definition.financialName),
);

const commoditySeriesTickerToFinancialName = (seriesTicker: string): CommodityFinancialName | undefined => {
    return COMMODITY_DEFINITIONS.find((definition) => definition.seriesPattern.test(seriesTicker))?.financialName;
};

export const seriesTickerToFinancialName = (seriesTicker: string): string | undefined => {
    const normalizedTicker = seriesTicker.toUpperCase();
    if (normalizedTicker.includes('NASDAQ')) return 'QQQ';
    if (normalizedTicker.includes('INX') && normalizedTicker !== 'KXJOINXAI') return 'SPX';
    if (normalizedTicker.includes('EURUSD')) return 'EURUSD';
    if (normalizedTicker.includes('USDJPY')) return 'USDJPY';
    if (normalizedTicker.includes('USDRUB')) return 'USDRUB';
    if (['BTCD', 'BTC', 'KXBTCD', 'KXBTC', 'KXBTC15M'].includes(normalizedTicker)) return 'BTC';
    if (['ETHD', 'ETH', 'KXETHD', 'KXETH', 'KXETH15M'].includes(normalizedTicker)) return 'ETH';
    if (['KXDOGED', 'KXDOGE', 'KXDOGEMAXW', 'KXDOGEMAXY', 'KXDOGE15M'].includes(normalizedTicker)) return 'DOGE';
    if (['KXBNBD', 'KXBNB', 'KXBNB15M'].includes(normalizedTicker)) return 'BNB';
    if (['KXHYPED', 'KXHYPE', 'KXHYPE15M'].includes(normalizedTicker)) return 'HYPE';
    if (['KXZECD', 'KXZEC', 'KXZEC15M'].includes(normalizedTicker)) return 'ZEC';
    if (['KXNEARD', 'KXNEAR', 'KXNEAR15M'].includes(normalizedTicker)) return 'NEAR';
    if (['KXSHIBAD', 'KXSHIBA', 'KXSHIBAMAXW', 'KXSHIBAMAXY'].includes(normalizedTicker)) return 'SHIBA';
    if (['SOLD', 'SOL', 'KXSOLD', 'KXSOL', 'KXSOL15M'].includes(normalizedTicker)) return 'SOL';
    if (['KXXRPD', 'KXXRP', 'KXXRP15M'].includes(normalizedTicker)) return 'XRP';
    // Crypto Leader tracks several assets rather than one, so it gets its own name:
    // enough for the event page to pick the financial/knockout chart branch, while staying
    // out of `FINANCIAL_NAME_TO_COIN` so nothing treats it as a single polled asset.
    if (normalizedTicker === 'KXCRYPTOLEAD15M') return 'CRYPTOLEAD';

    return commoditySeriesTickerToFinancialName(normalizedTicker);
};

export const isFinancialNameCrypto = (financialName?: string): boolean =>
    !!financialName && financialName in FINANCIAL_NAME_TO_COIN;

export const isFinancialNameCommodity = (financialName?: string): boolean =>
    !!financialName && COMMODITY_FINANCIAL_NAMES.has(financialName as CommodityFinancialName);

// Commodities whose feed is not named after our financial name; anything else resolves to `PYTH:<name>`.
// Keys follow svc-live-data's `staticQualifiedToShort` (adapters/commodities/pyth/pyth.go). WTI settles
// against the PYTHOIL index, and palladium and platinum run on the `Metal.XPD/USD` and `Metal.XPT/USD`
// spot feeds, so `PYTH:WTI`, `PYTH:PALLADIUM` and `PYTH:PLATINUM` are all rejected.
const LIVE_DATA_ASSET_OVERRIDES: Record<string, string> = {
    US2Y: 'InterestRate.US2Y',
    US5Y: 'InterestRate.US5Y',
    US10Y: 'InterestRate.US10Y',
    US30Y: 'InterestRate.US30Y',
    KPOWMID: 'Pyth.Index.KPOW-MIDTERMS',
    WTI: 'PYTHOIL',
    PALLADIUM: 'XPDUSD',
    PLATINUM: 'XPTUSD',
};

// Indices whose knockout feed is a svc-live-data commodity config addressed by its symbol rather than a Pyth
// feed. SPX is KXINX15M's: S&P DJI's own real-time S&P 500 from its GRIP feed. Like the Pyth feeds, the asset
// route serves these as raw ticks and rejects `twap`.
const INDEX_LIVE_DATA_ASSETS: Record<string, string> = {
    SPX: 'SPDJI_SP500',
};

/**
 * True when a financial name's knockout series is read raw from `/v1/live_data/assets/<asset>`, with no
 * public-docs lane and no `twap`: the Pyth-backed commodities, plus the indices in `INDEX_LIVE_DATA_ASSETS`.
 */
export const isFinancialNameAssetFeed = (financialName?: string): boolean =>
    isFinancialNameCommodity(financialName) || (!!financialName && financialName in INDEX_LIVE_DATA_ASSETS);

/**
 * The identifier `/v1/live_data/assets/<asset>` expects for a financial name.
 *
 * Crypto is addressed by the bare name. The Pyth-backed commodities are namespaced (`PYTH:GOLD`), and the
 * bare form is rejected, which is why a commodity slot read as having no asset feed at all. The indices in
 * `INDEX_LIVE_DATA_ASSETS` use their svc-live symbol.
 */
export const financialNameToLiveDataAsset = (financialName: string | undefined): string | undefined => {
    if (!financialName) return undefined;
    if (INDEX_LIVE_DATA_ASSETS[financialName]) return INDEX_LIVE_DATA_ASSETS[financialName];
    if (!isFinancialNameCommodity(financialName)) return financialName;
    return `PYTH:${LIVE_DATA_ASSET_OVERRIDES[financialName] ?? financialName}`;
};

// Indexes centred on zero rather than priced: a zero or negative reading is real, so the charts' `> 0`
// "not loaded yet" checks must not hide it, and a percent of the target means nothing.
const SIGNED_INDEX_NAMES = new Set<string>(['KPOWMID']);

export const isFinancialNameSignedIndex = (financialName?: string): boolean =>
    !!financialName && SIGNED_INDEX_NAMES.has(financialName);

// Indexes whose 15-minute markets settle on a trailing time-weighted average that the events route computes. KPOW
// Midterms settles on the 15-minute average, or the 60-second one for a market settling on election day; only the
// backend decides which. Their 15-minute charts read that route rather than the raw asset feed, so the chart plots the
// number the market settles on: each slot's history from its event, the live view from the series, for which the
// backend serves the slot trading now.
const EVENT_AVERAGED_SERIES: Record<string, string> = { KPOWMID: 'KXKPOWMID15M' };

/** The 15-minute series whose events route serves this index's settlement average, if it has one. */
export const financialNameEventAveragedSeries = (financialName?: string): string | undefined =>
    financialName ? EVENT_AVERAGED_SERIES[financialName] : undefined;

/** The KPOW Midterms ladder and up/down series (hourly, daily, 15-minute), which read in party terms, not as prices. */
export const isKpowMidtermsSeries = (seriesTicker: string | undefined): boolean =>
    !!seriesTicker && seriesTickerToFinancialName(seriesTicker) === 'KPOWMID';

/** The KPOW Midterms "at least" ladder (KXKPOWMID): one market per strike, labeled by its own subtitle. */
export const isKpowMidtermsLadder = (seriesTicker: string | undefined): boolean =>
    seriesTicker?.toUpperCase() === 'KXKPOWMID';

/**
 * A KPOW Midterms reading (or a signed difference between two readings) as the party it leans to, never as a sign:
 * negative is Democratic (`D+19.50`), positive Republican (`R+1.25`), and anything that rounds to zero at the shown
 * precision reads `Even`, so the label never says "D+0.00".
 */
export const formatKpowValue = (value: number, decimals = 2): string => {
    if (!Number.isFinite(value)) return '—';
    const magnitude = toVariableLengthFloat(Math.abs(value), decimals, decimals);
    if (Number(magnitude.replace(/,/g, '')) === 0) return 'Even';
    return `${value < 0 ? 'D' : 'R'}+${magnitude}`;
};

/** A KPOW strike as the market's rules state it (`-19.5` → `Index −19.5`), shown beside its party-terms target. */
export const formatKpowIndexLevel = (value: number): string =>
    `Index ${value < 0 ? '\u2212' : ''}${Math.abs(value).toLocaleString('en-US', { maximumFractionDigits: 4 })}`;

/**
 * True when a live value is a reading to show. The price charts use 0 for "not loaded yet", so a price must be
 * positive; any finite reading of a signed index counts, 0 ("Even") included, so its callers pass `undefined`
 * rather than that 0 placeholder when there is no reading.
 */
export const isChartReading = (value: number | undefined, financialName?: string): value is number =>
    value !== undefined && Number.isFinite(value) && (isFinancialNameSignedIndex(financialName) || value > 0);

/** True when a target or settled value is set: a positive price, or any level of a signed index (0 included). */
export const isChartLevel = (value: number | undefined, financialName?: string): value is number =>
    value !== undefined && Number.isFinite(value) && (isFinancialNameSignedIndex(financialName) || value > 0);

/**
 * True when a series tracks a single financial underlying with a live external
 * price feed (indices, FX, rates, WTI, single-asset crypto) — i.e. the event's
 * chart should show the underlying price, not a probability forecast.
 *
 * Crypto is matched against the exact-match allowlist inside
 * `seriesTickerToFinancialName`, never a bare `BTC`/`SOL`/`DOGE` substring, so
 * binary threshold or comparison series (`KXBTC2026250`, `KXSOL26500`,
 * `KXDOGEMAX1`, `KXBTCVSETH`, …) are correctly excluded and render the binary
 * chart instead of a broken underlying view.
 */
export const isFinancialUnderlyingSeries = (seriesTicker: string): boolean => {
    const ticker = seriesTicker.toUpperCase();
    if (ticker.includes('NASDAQ')) return true;
    if (ticker.includes('INX') && ticker !== 'KXJOINXAI') return true;
    if (ticker.includes('EURUSD')) return true;
    if (ticker.includes('USDJPY')) return true;
    if (ticker.includes('USDRUB')) return true;
    if (ticker.includes('TNOTE')) return true;
    if (ticker.includes('WTI')) return true;
    return isFinancialNameCrypto(seriesTickerToFinancialName(ticker));
};

export const eventToFinancialName = (event?: EventFinancialDetectionInput | null): string | undefined => {
    return seriesTickerToFinancialName(event?.seriesTicker ?? '');
};

export const isCompanyKpiEvent = (event?: CompanyKpiDetectionInput): boolean => {
    const subcategoriesByCategory = event?.series?.productMetadata?.subcategories;
    if (!subcategoriesByCategory) {
        return false;
    }

    return Object.values(subcategoriesByCategory).some((subcategories) =>
        subcategories.some((subcategory) => subcategory.trim().toLowerCase() === 'kpis'),
    );
};

export const seriesTickerToIndicesName = (seriesTicker: string): string | undefined => {
    if (['PRESINDEXD', 'CONTROL'].includes(seriesTicker)) return 'ELECTIONS_PRES_INDICES';

    return undefined;
};
